/**
 * Promotion lifecycle (master D-4): the phase, the robots value and the HTTP
 * status a promotion page should have, and the date texts it shows (web plan
 * W0.9).
 *
 * STOPGAP. The backend owns this answer: once it exposes `phase` and
 * `seo { robots }` (backend B1.4), pages must use those and only fall back to
 * this module for data that lacks them. The rules here mirror D-4 exactly so
 * the switch changes nothing visible.
 *
 * Pure module (no `window`, no React).
 */
import { DEFAULT_LOCALE, type Locale } from './locales';
import {
  formatDayMonth,
  formatDayMonthYear,
  formatLocalDayRange,
  toBoundaryDate,
  toZonedIsoString,
  zonedParts,
  type DateInput,
} from './text';

export type PromotionPhase = 'upcoming' | 'active' | 'ended';

/** Robots for every indexable page (and the base that `unavailable_after` extends). */
export const INDEX_ROBOTS = 'index, follow, max-image-preview:large';

/** Robots for a page that must stay out of the index but keep passing links. */
export const NOINDEX_ROBOTS = 'noindex, follow';

/** D-4 defaults (the backend thresholds file holds the real values from B1.4). */
export const UNAVAILABLE_AFTER_DAYS = 7;
export const GONE_AFTER_DAYS = 90;

const DAY_MS = 86_400_000;

export interface PromotionDates {
  startDate?: DateInput;
  endDate?: DateInput;
  /**
   * The backend's phase (B1.4 `UPCOMING` / `ACTIVE` / `ENDED`), when the data
   * carries it (the promotion detail query): it wins over the dates, so the
   * page's title, notices and JSON-LD agree with the backend's `seo.robots`.
   */
  phase?: string | null;
}

const BACKEND_PHASES: ReadonlyMap<string, PromotionPhase> = new Map([
  ['UPCOMING', 'upcoming'],
  ['ACTIVE', 'active'],
  ['ENDED', 'ended'],
]);

/**
 * A promotion's dates as instants. The backend stores a full ISO date-time,
 * the end being the last instant of its (inclusive) day; rows written before
 * it normalised writes can hold a bare `YYYY-MM-DD`, read here in the locale's
 * time zone as 00:00 for the start and 23:59:59.999 for the end (see
 * `toBoundaryDate`). A missing or unparseable date is `null`. Every date rule
 * below (phase, robots, titles, notices, JSON-LD) reads the dates through this.
 */
export function promotionWindow(
  promo: PromotionDates,
  locale: Locale = DEFAULT_LOCALE,
): { start: Date | null; end: Date | null } {
  return {
    start: toBoundaryDate(promo.startDate, 'start', locale.timeZone),
    end: toBoundaryDate(promo.endDate, 'end', locale.timeZone),
  };
}

/**
 * The backend's `phase` when the data has one (see PromotionDates), else the
 * phase from DATES ONLY — never the stored `status` or `isUpcoming`:
 * `startDate > now` → upcoming, `endDate ≤ now` → ended, else active (dates
 * from `promotionWindow`). A missing or unparseable date is treated as
 * absent, so a promotion with no end date stays active once it has started.
 */
export function promotionPhase(
  promo: PromotionDates,
  now: Date = new Date(),
  locale: Locale = DEFAULT_LOCALE,
): PromotionPhase {
  const backend = typeof promo.phase === 'string' ? BACKEND_PHASES.get(promo.phase) : undefined;
  if (backend) return backend;
  const { start, end } = promotionWindow(promo, locale);
  if (start && start.getTime() > now.getTime()) return 'upcoming';
  if (end && end.getTime() <= now.getTime()) return 'ended';
  return 'active';
}

/** True when at least one of `promos` is active at `now` (the "Today" rule in titles). */
export function hasActivePromotion(
  promos: readonly PromotionDates[] | null | undefined,
  now: Date = new Date(),
  locale: Locale = DEFAULT_LOCALE,
): boolean {
  return (promos ?? []).some((p) => promotionPhase(p, now, locale) === 'active');
}

/**
 * Listed promotions split by phase, each in the given order and once (by id):
 * the running ones and the upcoming ones. Ended ones are dropped: the public
 * feeds already leave them out, and a cached copy must not show one as
 * current. The brand page lists `active` under "Active offers" and says "No
 * active offers right now" when it is empty, even while `upcoming` has some
 * (web plan W0.10), matching the "Today" rule of its title.
 */
export function offersByPhase<T extends PromotionDates & { id: string }>(
  promos: readonly (T | null | undefined)[] | null | undefined,
  now: Date = new Date(),
  locale: Locale = DEFAULT_LOCALE,
): { active: T[]; upcoming: T[] } {
  const active: T[] = [];
  const upcoming: T[] = [];
  const seen = new Set<string>();
  for (const promo of promos ?? []) {
    if (!promo || seen.has(promo.id)) continue;
    seen.add(promo.id);
    const phase = promotionPhase(promo, now, locale);
    if (phase === 'active') active.push(promo);
    else if (phase === 'upcoming') upcoming.push(promo);
  }
  return { active, upcoming };
}

/**
 * A fetched page of promotions with the running ones first (in their given
 * order), then the upcoming ones, soonest start first. Re-orders only the page
 * it is given (the backend's page boundaries are untouched), and is shared by
 * the SPA's list hooks and the SEO server so the head's ItemList matches.
 */
export function liveFirst<T extends PromotionDates>(
  promos: readonly T[],
  now: Date = new Date(),
  locale: Locale = DEFAULT_LOCALE,
): T[] {
  const live: T[] = [];
  const upcoming: { promo: T; start: number }[] = [];
  for (const promo of promos) {
    if (promotionPhase(promo, now, locale) === 'upcoming') {
      upcoming.push({
        promo,
        start: promotionWindow(promo, locale).start?.getTime() ?? Number.MAX_SAFE_INTEGER,
      });
    } else live.push(promo);
  }
  // Array.prototype.sort is stable: equal starts keep the backend order.
  upcoming.sort((a, b) => a.start - b.start);
  return [...live, ...upcoming.map((u) => u.promo)];
}

/**
 * A robots value: `noindex, follow`, or the index base plus any extra
 * directives (e.g. `unavailable_after: …`). Extras never apply to a noindex
 * page — D-4's ended pages carry the bare `noindex, follow`.
 */
export function robotsValue({
  noindex = false,
  extra,
}: { noindex?: boolean; extra?: string | null } = {}): string {
  if (noindex) return NOINDEX_ROBOTS;
  const more = extra?.trim();
  return more ? `${INDEX_ROBOTS}, ${more}` : INDEX_ROBOTS;
}

export interface PromotionLifecycle {
  phase: PromotionPhase;
  /** What the SEO server should answer: 410 once ended more than 90 days ago. */
  status: 200 | 410;
  /** Whether the promotion belongs in the sitemap (upcoming and active only). */
  inSitemap: boolean;
  noindex: boolean;
  /** `unavailable_after: 2026-10-07T23:59:59+05:00`, or null. */
  robotsExtra: string | null;
  /** The exact D-4 robots string. */
  robots: string;
}

/**
 * D-4 for one promotion at `now`:
 *
 * | phase                | robots                                                         | status |
 * |----------------------|----------------------------------------------------------------|--------|
 * | upcoming / active    | index, follow, max-image-preview:large, unavailable_after: E+7d | 200    |
 * | ended ≤ 7 days       | same (still index)                                              | 200    |
 * | ended 8–90 days      | noindex, follow (explicit — Bing ignores unavailable_after)     | 200    |
 * | ended > 90 days      | noindex, follow (the server answers 410)                        | 410    |
 *
 * `unavailable_after` is emitted whenever an end date exists, as ISO 8601 with
 * the locale's offset.
 */
export function promotionLifecycle(
  promo: PromotionDates,
  now: Date = new Date(),
  locale: Locale = DEFAULT_LOCALE,
): PromotionLifecycle {
  const phase = promotionPhase(promo, now, locale);
  const { end } = promotionWindow(promo, locale);
  const sinceEnd = phase === 'ended' && end ? now.getTime() - end.getTime() : 0;

  const noindex = sinceEnd > UNAVAILABLE_AFTER_DAYS * DAY_MS;
  const robotsExtra =
    !noindex && end
      ? `unavailable_after: ${toZonedIsoString(end.getTime() + UNAVAILABLE_AFTER_DAYS * DAY_MS, locale.timeZone)}`
      : null;

  return {
    phase,
    status: sinceEnd > GONE_AFTER_DAYS * DAY_MS ? 410 : 200,
    inSitemap: phase !== 'ended',
    noindex,
    robotsExtra,
    robots: robotsValue({ noindex, extra: robotsExtra }),
  };
}

// ─── What the page shows (master D-4, "Page shows") ─────────────────────────

/** A promotion's dates, plus the backend's `lastVerifiedAt` once it exists (backend B3.5). */
export interface PromotionNoticeInput extends PromotionDates {
  lastVerifiedAt?: DateInput;
}

/**
 * The date texts a promotion page shows, all from the dates in the locale's
 * time zone (the same reading as the title, the robots and the JSON-LD), and
 * never from the stored `status` or `isUpcoming`: the backend's status getter
 * still says UPCOMING / EXPIRED, which would put a second, raw badge next to
 * the phase's own.
 */
export interface PromotionNotice {
  phase: PromotionPhase;
  /** The page's one lifecycle badge: "Starts 1 Oct 2026" or "Ended 30 Sep 2026"; null while active. */
  badge: string | null;
  /** "Valid till 30 Sep 2026" while active with an end date. */
  validTill: string | null;
  /** The whole period: "1 – 30 Sep 2026", "From 1 Sep 2026", "Until 30 Sep 2026"; '' without dates. */
  period: string;
  /** Ended: "This Mocciani offer ended on 30 Sep 2026." ("This offer …" without a merchant). */
  endedNotice: string | null;
  /**
   * Active with no end date: "Last verified 20 Sep 2026" from the backend's
   * `lastVerifiedAt` (B3.5); null until the backend sends it.
   */
  lastVerified: string | null;
}

/** `YYYY-MM-DD` of `date`'s calendar day in `timeZone`. */
function zonedDay(date: Date, timeZone: string): string {
  const p = zonedParts(date, timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** "1 – 30 Sep 2026", "25 Sep – 10 Oct 2026", "From 1 Sep 2026", "Until 30 Sep 2026", or ''. */
export function promotionPeriod(promo: PromotionDates, locale: Locale = DEFAULT_LOCALE): string {
  const { start, end } = promotionWindow(promo, locale);
  if (start && end && end.getTime() >= start.getTime()) {
    return formatLocalDayRange(zonedDay(start, locale.timeZone), zonedDay(end, locale.timeZone));
  }
  if (start) return `From ${formatDayMonthYear(start, locale.timeZone)}`;
  if (end) return `Until ${formatDayMonthYear(end, locale.timeZone)}`;
  return '';
}

/**
 * `promotionPeriod` without the year or weekday, for a card's date line:
 * "1 – 30 Sep", "25 Sep – 10 Oct", "11 Nov" (one day), "From 1 Sep",
 * "Until 30 Sep", or ''. The same days as the page's period (the locale's
 * time zone, not the browser's, which put a Karachi-midnight start a day
 * early for a US visitor or Googlebot).
 */
export function promotionPeriodShort(promo: PromotionDates, locale: Locale = DEFAULT_LOCALE): string {
  const tz = locale.timeZone;
  const { start, end } = promotionWindow(promo, locale);
  if (start && end && end.getTime() >= start.getTime()) {
    const s = zonedParts(start, tz);
    const e = zonedParts(end, tz);
    const endText = formatDayMonth(end, tz);
    if (s.year === e.year && s.month === e.month) {
      return s.day === e.day ? endText : `${s.day} – ${endText}`;
    }
    return `${formatDayMonth(start, tz)} – ${endText}`;
  }
  if (start) return `From ${formatDayMonth(start, tz)}`;
  if (end) return `Until ${formatDayMonth(end, tz)}`;
  return '';
}

/**
 * What a promotion page says about its dates at `now` (master D-4): upcoming
 * → "Starts {date}"; ended → "This {Merchant} offer ended on {date}" above
 * the fold; no end date → "Last verified {date}" once the backend has it.
 * `merchantName` is the cleaned brand (or first shop) name, see
 * `promotionMerchant` in ./structuredData.
 */
export function promotionNotice(
  promo: PromotionNoticeInput,
  merchantName: string | null | undefined,
  now: Date = new Date(),
  locale: Locale = DEFAULT_LOCALE,
): PromotionNotice {
  const phase = promotionPhase(promo, now, locale);
  const tz = locale.timeZone;
  const dates = promotionWindow(promo, locale);
  const start = formatDayMonthYear(dates.start, tz);
  const end = formatDayMonthYear(dates.end, tz);
  const verified = !end ? formatDayMonthYear(promo.lastVerifiedAt, tz) : '';
  const who = merchantName?.trim() ? `This ${merchantName.trim()} offer` : 'This offer';
  return {
    phase,
    badge: phase === 'upcoming' ? `Starts ${start}` : phase === 'ended' ? `Ended ${end}` : null,
    validTill: phase === 'active' && end ? `Valid till ${end}` : null,
    period: promotionPeriod(promo, locale),
    endedNotice: phase === 'ended' ? `${who} ended on ${end}.` : null,
    lastVerified: phase === 'active' && verified ? `Last verified ${verified}` : null,
  };
}
